import { FC, useEffect } from 'react';
import TableModule from './TableModule';
import {
  storeEngineeringCostConfig,
  CoreInfoRelateNames,
  StoreEngineeringCostNames
} from '../ts-config';
import { Form } from 'antd';
import { isNotEmpty } from '@lhb/func';
import { fieldNameStoreEngineeringCost } from './fieldname';
import { fixIntParam } from './transfer2value';

/**
 * @Description 店铺工程成本
 */
const StoreEngineeringCost: FC<{
  form: any;
  formItemData: any;
  disabled?: boolean;
  onChange: any;
}> = ({
  form,
  formItemData,
  disabled,
  onChange
}) => {
  // 提报合同面积
  const basicUsableArea: any = Form.useWatch(
    CoreInfoRelateNames.basicUsableArea,
    form
  );

  // 外立面工程单价
  const facadePrice: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.facadePrice),
    form
  );
  // 外立面工程总价
  const facadeTotal: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.facadeTotal),
    form
  );
  // 装修工程单价
  const decorationPrice: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.decorationPrice),
    form
  );
  // 装修工程总价
  const decorationTotal: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.decorationTotal),
    form
  );
  // 消防工程单价
  const firePrice: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.firePrice),
    form
  );
  // 消防工程总价
  const fireTotal: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.fireTotal),
    form
  );

  // 空调工程单价
  const airPrice: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.airPrice),
    form
  );
  // 空调工程总价
  const airTotal: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.airTotal),
    form
  );
  // 网络及音响工程单价
  const networkAudioPrice: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.networkAudioPrice),
    form
  );
  // 网络及音响工程总价
  const networkAudioTotal: any = Form.useWatch(
    fieldNameStoreEngineeringCost(StoreEngineeringCostNames.networkAudioTotal),
    form
  );

  // 店铺工程费用
  useEffect(() => {
    const name = fieldNameStoreEngineeringCost(StoreEngineeringCostNames.storeEngineeringCost);
    const val: any = calcStoreEngineeringCost(facadeTotal, decorationTotal, fireTotal, airTotal, networkAudioTotal);
    form.setFieldValue(name, val);
  }, [facadeTotal, decorationTotal, fireTotal, airTotal, networkAudioTotal]);

  /**
   * @description 店铺工程费用
   * @param facadeTotal
   * @param decorationTotal
   * @param fireTotal
   * @param airTotal
   * @param networkAudioTotal
   * @return
   *  （外立面工程总价+装修工程总价+消防工程总价+空调工程总价+网络及音响工程总价）/1.1./2
      店铺工程费用均摊到第一年和第二年，单位：元
   */
  const calcStoreEngineeringCost = (facadeTotal, decorationTotal, fireTotal, airTotal, networkAudioTotal) => {
    let val: any = null;
    isNotEmpty(facadeTotal) && (val += (+facadeTotal));
    isNotEmpty(decorationTotal) && (val += (+decorationTotal));
    isNotEmpty(fireTotal) && (val += (+fireTotal));
    isNotEmpty(airTotal) && (val += (+airTotal));
    isNotEmpty(networkAudioTotal) && (val += (+networkAudioTotal));

    isNotEmpty(val) && (val = fixIntParam(val / 1.1 / 2));
    return val;
  };

  // 外立面工程总价
  useEffect(() => {
    const name = fieldNameStoreEngineeringCost(StoreEngineeringCostNames.facadeTotal);
    const val: any = calcFacadeTotal(facadePrice, basicUsableArea);
    form.setFieldValue(name, val);
  }, [facadePrice, basicUsableArea]);

  /**
   * @description 外立面工程总价
   * @param facadePrice
   * @param basicUsableArea
   * @return 外立面工程单价*提报合同面积
   */
  const calcFacadeTotal = (facadePrice, basicUsableArea) => {
    return calTotalPrice(facadePrice, basicUsableArea);
  };

  // 装修工程总价
  useEffect(() => {
    const name = fieldNameStoreEngineeringCost(StoreEngineeringCostNames.decorationTotal);
    const val: any = calcDecorationTotal(decorationPrice, basicUsableArea);
    form.setFieldValue(name, val);
  }, [decorationPrice, basicUsableArea]);

  /**
     * @description 装修工程总价
     * @param decorationPrice
     * @param basicUsableArea
     * @return
     *   装修工程单价*提报合同面积
     */
  const calcDecorationTotal = (decorationPrice, basicUsableArea) => {
    return calTotalPrice(decorationPrice, basicUsableArea);
  };

  // 消防工程总价
  useEffect(() => {
    const name = fieldNameStoreEngineeringCost(StoreEngineeringCostNames.fireTotal);
    const val: any = calcFireTotal(firePrice, basicUsableArea);
    form.setFieldValue(name, val);
  }, [firePrice, basicUsableArea]);

  /**
   * @description 消防工程总价
   * @param firePrice
   * @param basicUsableArea
   * @return
   *   消防工程单价*提报合同面积
   */
  const calcFireTotal = (firePrice, basicUsableArea) => {
    return calTotalPrice(firePrice, basicUsableArea);
  };

  // 空调工程总价
  useEffect(() => {
    const name = fieldNameStoreEngineeringCost(StoreEngineeringCostNames.airTotal);
    const val: any = calcAirTotal(airPrice, basicUsableArea);
    form.setFieldValue(name, val);
  }, [airPrice, basicUsableArea]);

  /**
   * @description 空调工程总价
   * @param airPrice
   * @param basicUsableArea
   * @return
   *   空调工程单价*提报合同面积
   */
  const calcAirTotal = (airPrice, basicUsableArea) => {
    return calTotalPrice(airPrice, basicUsableArea);
  };

  // 网络及音响工程总价
  useEffect(() => {
    const name = fieldNameStoreEngineeringCost(StoreEngineeringCostNames.networkAudioTotal);
    const val: any = calcNetworkAudioTotal(networkAudioPrice, basicUsableArea);
    form.setFieldValue(name, val);
  }, [networkAudioPrice, basicUsableArea]);

  /**
   * @description 网络及音响工程总价
   * @param networkAudioPrice
   * @param basicUsableArea
   * @return
   *   网络及音响单价*提报合同面积
   */
  const calcNetworkAudioTotal = (networkAudioPrice, basicUsableArea) => {
    return calTotalPrice(networkAudioPrice, basicUsableArea);
  };

  /**
   * @description 单项工程总价
   * @param price 单项工程单价
   * @param area 面积
   * @return 单项工程单价*面积
   */
  const calTotalPrice = (price, area) => {
    return isNotEmpty(price) && isNotEmpty(area) ? fixIntParam((+price) * (+area)) : null;
  };

  return (
    <TableModule
      moduleConfig={storeEngineeringCostConfig}
      form={form}
      formItemData={formItemData}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

export default StoreEngineeringCost;
